import {
  ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS,
  ACTIONFLOW_DATASOURCE_SCENARIOS,
  ACTIONFLOW_ONTOLOGY_V1_CONTRACT,
  ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS,
  ACTIONFLOW_PRIMITIVE_TAXONOMY,
  ACTIONFLOW_STATE_SCOPE_TAXONOMY,
  ACTIONFLOW_RELEASE_ACTION_COVERAGE,
  ACTIONFLOW_TRACE_CONTRACT,
  AGENTIC_CONFORMANCE_RELEASE_CONTRACT,
  EXPRESSION_ONTOLOGY_CONTRACT,
  FALLBACK_PROMOTION_LIFECYCLE_STATUSES,
  KG_OBSERVATION_CONTRACT,
} from "./actionflow-contract-ontology";

export interface ContractValidationResult {
  ok: boolean;
  errors: string[];
}

export interface ActionFlowExpressionOntologyValidationOptions {
  nodePaths?: readonly string[];
  topology?: unknown;
  path?: string;
}

const ACTIONFLOW_TOPOLOGY_CONTRACT_VERSION =
  "studio-actionflow-state-topology.v1";

const ACTIONFLOW_TOPOLOGY_STATE_SCOPES = [
  "local",
  "appData",
  "computed",
  "datasource",
  "route",
  "env",
  "props",
  "_results",
] as const;

const ACTIONFLOW_TOPOLOGY_STATE_NODE_KINDS = [
  "local-state",
  "global-state",
  "computed-projection",
  "datasource",
  "route-context",
  "environment",
  "props",
  "result",
] as const;

const ACTIONFLOW_TOPOLOGY_STATE_SCOPE_SET = new Set<string>(
  ACTIONFLOW_TOPOLOGY_STATE_SCOPES,
);

type ActionFlowPrimitiveKind =
  keyof typeof ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS;

const EXPRESSION_NODE_BACKED_PREFIXES = new Set(["local", "appData", "computed"]);
const EXPRESSION_ALLOWED_PREFIXES = new Set<string>(
  EXPRESSION_ONTOLOGY_CONTRACT.canonicalPrefixes,
);
const EXPRESSION_LEGACY_PREFIXES = new Set<string>(
  EXPRESSION_ONTOLOGY_CONTRACT.legacyPrefixes,
);
const EXPRESSION_ALLOWED_ACCESSORS = new Set<string>(
  EXPRESSION_ONTOLOGY_CONTRACT.accessors,
);

export function validateActionFlowExpressionOntology(
  value: string,
  options: ActionFlowExpressionOntologyValidationOptions = {},
): ContractValidationResult {
  const errors: string[] = [];
  const nodes =
    options.topology !== undefined
      ? topologyNodes(options.topology)
      : nodesFromPaths(options.nodePaths ?? []);
  validateExpressionOntology(
    value,
    nodes,
    errors,
    options.path ?? "expression",
  );
  return { ok: errors.length === 0, errors };
}

export function validateActionFlowTraceContract(
  value: unknown,
): ContractValidationResult {
  const errors: string[] = [];
  const trace = asRecord(value);
  if (!trace) return fail("trace must be an object");
  expectLiteral(
    trace,
    "contractVersion",
    ACTIONFLOW_TRACE_CONTRACT.version,
    errors,
  );
  expectString(
    trace,
    "traceId",
    errors,
    ACTIONFLOW_TRACE_CONTRACT.traceIdPattern,
  );
  expectString(trace, "flowName", errors);
  expectOneOf(trace, "trigger", ACTIONFLOW_TRACE_CONTRACT.triggers, errors);
  expectOneOf(trace, "status", ACTIONFLOW_TRACE_CONTRACT.statuses, errors);
  expectRecord(trace, "startState", errors);
  const finalState = expectRecord(trace, "finalState", errors);
  if (
    ["error", "cancelled", "timed-out"].includes(String(trace.status)) &&
    typeof trace.error !== "string"
  ) {
    errors.push(`${String(trace.status)} traces require error`);
  }
  const steps = expectArray(trace, "steps", errors);
  const latestStateChanges = new Map<string, unknown>();
  for (const [index, rawStep] of steps.entries()) {
    const step = asRecord(rawStep);
    if (!step) {
      errors.push(`steps[${index}] must be an object`);
      continue;
    }
    expectNumber(step, "index", errors);
    if (step.index !== index) {
      errors.push(`steps[${index}].index must equal ${index}`);
    }
    expectString(step, "type", errors);
    expectOneOf(step, "status", ACTIONFLOW_TRACE_CONTRACT.stepStatuses, errors);
    if (step.type === "basic") {
      expectString(step, "action", errors);
    }
    if (step.status === "error" && typeof step.error !== "string") {
      errors.push(`steps[${index}].error is required when status is error`);
    }
    if ("parentIndex" in step) {
      if (
        typeof step.parentIndex !== "number" ||
        !Number.isInteger(step.parentIndex)
      ) {
        errors.push(
          `steps[${index}].parentIndex must be an integer when present`,
        );
      } else if (step.parentIndex < 0 || step.parentIndex >= index) {
        errors.push(
          `steps[${index}].parentIndex must reference an earlier step`,
        );
      }
    }
    if ("stateChanges" in step) {
      const changes = expectArray(step, "stateChanges", errors);
      for (const [changeIndex, rawChange] of changes.entries()) {
        const change = asRecord(rawChange);
        if (!change) {
          errors.push(
            `steps[${index}].stateChanges[${changeIndex}] must be an object`,
          );
          continue;
        }
        expectString(change, "path", errors);
        if (!("before" in change))
          errors.push(
            `steps[${index}].stateChanges[${changeIndex}].before is required`,
          );
        if (!("after" in change))
          errors.push(
            `steps[${index}].stateChanges[${changeIndex}].after is required`,
          );
        if (typeof change.path === "string" && "after" in change) {
          latestStateChanges.set(change.path, change.after);
        }
      }
    } else {
      errors.push(`steps[${index}].stateChanges is required`);
    }
  }
  if (finalState) {
    for (const [path, after] of latestStateChanges) {
      const finalValue = readPath(finalState, path);
      if (!deepEqual(finalValue, after)) {
        errors.push(
          `latest stateChanges after for ${path} must match finalState.${path}`,
        );
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

export function validateActionFlowTopologyContract(
  value: unknown,
): ContractValidationResult {
  const errors: string[] = [];
  const topology = asRecord(value);
  if (!topology) return fail("actionFlowTopology must be an object");

  expectLiteral(
    topology,
    "schemaVersion",
    ACTIONFLOW_TOPOLOGY_CONTRACT_VERSION,
    errors,
  );
  expectString(topology, "source", errors);
  expectString(topology, "generatedAt", errors);

  const rawNodes = expectArray(topology, "nodes", errors);
  const rawRelations = expectArray(topology, "relations", errors);
  const rawPrimitives = expectArray(topology, "primitives", errors);
  const rawEffectBoundaries = expectArray(topology, "effectBoundaries", errors);

  const nodes = new Map<string, Record<string, unknown>>();
  const effectBoundaries = new Map<string, Record<string, unknown>>();
  const primitives = new Map<string, Record<string, unknown>>();
  const primitiveKinds = new Map<string, ActionFlowPrimitiveKind>();

  for (const [index, rawNode] of rawNodes.entries()) {
    const path = `nodes[${index}]`;
    const node = asRecord(rawNode);
    if (!node) {
      errors.push(`${path} must be an object`);
      continue;
    }
    const id = expectStringValue(node, "id", errors, `${path}.id`);
    const kind = expectStringValue(node, "kind", errors, `${path}.kind`);
    const scope = expectStringValue(node, "scope", errors, `${path}.scope`);
    expectStringValue(node, "path", errors, `${path}.path`);
    expectBoolean(node, "writable", errors, `${path}.writable`);
    expectStringArray(node, "ontologyRefs", errors, `${path}.ontologyRefs`);
    expectStringArray(node, "evidenceRefs", errors, `${path}.evidenceRefs`);
    if (kind)
      expectKnown(
        kind,
        ACTIONFLOW_TOPOLOGY_STATE_NODE_KINDS,
        `${path}.kind`,
        errors,
      );
    if (scope)
      expectKnown(
        scope,
        ACTIONFLOW_TOPOLOGY_STATE_SCOPES,
        `${path}.scope`,
        errors,
      );
    if (scope === "local" && typeof node.ownerBlockName !== "string") {
      errors.push(`${path}.ownerBlockName is required for local state nodes`);
    }
    if ("ownerBlockName" in node)
      expectOptionalString(
        node,
        "ownerBlockName",
        errors,
        `${path}.ownerBlockName`,
      );
    if ("datasourceName" in node)
      expectOptionalString(
        node,
        "datasourceName",
        errors,
        `${path}.datasourceName`,
      );
    if (!id) continue;
    if (nodes.has(id)) errors.push(`${path}.id duplicates ${id}`);
    nodes.set(id, node);
  }

  for (const [index, rawBoundary] of rawEffectBoundaries.entries()) {
    const path = `effectBoundaries[${index}]`;
    const boundary = asRecord(rawBoundary);
    if (!boundary) {
      errors.push(`${path} must be an object`);
      continue;
    }
    const id = expectStringValue(boundary, "id", errors, `${path}.id`);
    const kind = expectStringValue(boundary, "kind", errors, `${path}.kind`);
    expectBoolean(boundary, "sideEffect", errors, `${path}.sideEffect`);
    expectStringArray(boundary, "evidenceRefs", errors, `${path}.evidenceRefs`);
    if (kind)
      expectKnown(
        kind,
        ACTIONFLOW_PRIMITIVE_TAXONOMY.effectBoundaryKinds,
        `${path}.kind`,
        errors,
      );
    if ("requiredCapability" in boundary) {
      expectOptionalString(
        boundary,
        "requiredCapability",
        errors,
        `${path}.requiredCapability`,
      );
    }
    if (!id) continue;
    if (effectBoundaries.has(id)) errors.push(`${path}.id duplicates ${id}`);
    effectBoundaries.set(id, boundary);
  }

  for (const [index, rawPrimitive] of rawPrimitives.entries()) {
    const path = `primitives[${index}]`;
    const primitive = asRecord(rawPrimitive);
    if (!primitive) {
      errors.push(`${path} must be an object`);
      continue;
    }
    const id = expectStringValue(primitive, "id", errors, `${path}.id`);
    const kind = expectStringValue(primitive, "kind", errors, `${path}.kind`);
    expectStringValue(primitive, "label", errors, `${path}.label`);
    const required = expectBoolean(
      primitive,
      "required",
      errors,
      `${path}.required`,
    );
    const inputNodeIds = expectStringArray(
      primitive,
      "inputNodeIds",
      errors,
      `${path}.inputNodeIds`,
    );
    const outputNodeIds = expectStringArray(
      primitive,
      "outputNodeIds",
      errors,
      `${path}.outputNodeIds`,
    );
    const effectBoundaryRefs = expectStringArray(
      primitive,
      "effectBoundaryRefs",
      errors,
      `${path}.effectBoundaryRefs`,
    );
    const ontologyRefs = expectStringArray(
      primitive,
      "ontologyRefs",
      errors,
      `${path}.ontologyRefs`,
    );
    const evidenceRefs = expectStringArray(
      primitive,
      "evidenceRefs",
      errors,
      `${path}.evidenceRefs`,
    );
    const compilerParamBindings =
      "compilerParamBindings" in primitive
        ? expectArray(primitive, "compilerParamBindings", errors)
        : null;
    const compilerTargetAction =
      "compilerTargetAction" in primitive
        ? expectOptionalString(
            primitive,
            "compilerTargetAction",
            errors,
            `${path}.compilerTargetAction`,
          )
        : null;
    const compilerTargetStepType =
      "compilerTargetStepType" in primitive
        ? expectOptionalString(
            primitive,
            "compilerTargetStepType",
            errors,
            `${path}.compilerTargetStepType`,
          )
        : null;

    if (
      !kind ||
      !expectKnown(
        kind,
        ACTIONFLOW_PRIMITIVE_TAXONOMY.primitiveKinds,
        `${path}.kind`,
        errors,
      )
    ) {
      continue;
    }

    const primitiveKind = kind as ActionFlowPrimitiveKind;
    const compilerContract =
      ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS[primitiveKind];
    if (required === true && ontologyRefs.length === 0) {
      errors.push(
        `${path}.ontologyRefs must include at least one ontology reference for required primitives`,
      );
    }
    if (required === true && evidenceRefs.length === 0) {
      errors.push(
        `${path}.evidenceRefs must include at least one evidence reference for required primitives`,
      );
    }
    if (required === true && compilerParamBindings === null) {
      errors.push(
        `${path}.compilerParamBindings is required for required primitives`,
      );
    }
    if (compilerTargetAction) {
      if (
        !(compilerContract.compilerTargetActions as readonly string[]).includes(
          compilerTargetAction,
        )
      ) {
        errors.push(
          `${path}.compilerTargetAction ${compilerTargetAction} is not allowed for primitive ${primitiveKind}`,
        );
      }
    } else if (compilerContract.compilerTargetActions.length > 0) {
      errors.push(
        `${path}.compilerTargetAction is required for primitive ${primitiveKind}`,
      );
    }
    if (compilerTargetStepType) {
      if (
        !(
          compilerContract.compilerTargetStepTypes as readonly string[]
        ).includes(compilerTargetStepType)
      ) {
        errors.push(
          `${path}.compilerTargetStepType ${compilerTargetStepType} is not allowed for primitive ${primitiveKind}`,
        );
      }
    } else if (compilerContract.compilerTargetStepTypes.length > 0) {
      errors.push(
        `${path}.compilerTargetStepType is required for primitive ${primitiveKind}`,
      );
    }

    for (const [nodeIndex, nodeId] of [
      ...inputNodeIds,
      ...outputNodeIds,
    ].entries()) {
      if (!nodes.has(nodeId)) {
        errors.push(
          `${path}.nodeRefs[${nodeIndex}] references missing node ${nodeId}`,
        );
      }
    }
    for (const [boundaryIndex, boundaryRef] of effectBoundaryRefs.entries()) {
      const boundary = effectBoundaries.get(boundaryRef);
      if (!boundary) {
        errors.push(
          `${path}.effectBoundaryRefs[${boundaryIndex}] references missing boundary ${boundaryRef}`,
        );
        continue;
      }
      const boundaryKind = String(boundary.kind);
      if (
        !(compilerContract.effectBoundaryKinds as readonly string[]).includes(
          boundaryKind,
        )
      ) {
        errors.push(
          `${path}.effectBoundaryRefs[${boundaryIndex}] kind ${boundaryKind} is not allowed for primitive ${primitiveKind}`,
        );
      }
    }
    if (compilerParamBindings !== null) {
      validateCompilerParamBindings(
        compilerParamBindings,
        compilerContract.paramRoles,
        nodes,
        errors,
        `${path}.compilerParamBindings`,
      );
    }
    if (!id) continue;
    if (primitives.has(id)) errors.push(`${path}.id duplicates ${id}`);
    primitives.set(id, primitive);
    primitiveKinds.set(id, primitiveKind);
  }

  for (const [index, rawRelation] of rawRelations.entries()) {
    const path = `relations[${index}]`;
    const relation = asRecord(rawRelation);
    if (!relation) {
      errors.push(`${path} must be an object`);
      continue;
    }
    expectStringValue(relation, "id", errors, `${path}.id`);
    const kind = expectStringValue(relation, "kind", errors, `${path}.kind`);
    const sourceNodeId = expectStringValue(
      relation,
      "sourceNodeId",
      errors,
      `${path}.sourceNodeId`,
    );
    const targetNodeId = expectStringValue(
      relation,
      "targetNodeId",
      errors,
      `${path}.targetNodeId`,
    );
    const primitiveRef = expectStringValue(
      relation,
      "primitiveRef",
      errors,
      `${path}.primitiveRef`,
    );
    const evidenceRefs = expectStringArray(
      relation,
      "evidenceRefs",
      errors,
      `${path}.evidenceRefs`,
    );
    const effectBoundaryRef =
      "effectBoundaryRef" in relation
        ? expectOptionalString(
            relation,
            "effectBoundaryRef",
            errors,
            `${path}.effectBoundaryRef`,
          )
        : null;

    if (kind)
      expectKnown(
        kind,
        ACTIONFLOW_PRIMITIVE_TAXONOMY.stateRelationKinds,
        `${path}.kind`,
        errors,
      );
    if (sourceNodeId && !nodes.has(sourceNodeId))
      errors.push(
        `${path}.sourceNodeId references missing node ${sourceNodeId}`,
      );
    if (targetNodeId && !nodes.has(targetNodeId))
      errors.push(
        `${path}.targetNodeId references missing node ${targetNodeId}`,
      );
    if (evidenceRefs.length === 0)
      errors.push(
        `${path}.evidenceRefs must include at least one evidence reference`,
      );
    if (effectBoundaryRef && !effectBoundaries.has(effectBoundaryRef)) {
      errors.push(
        `${path}.effectBoundaryRef references missing boundary ${effectBoundaryRef}`,
      );
    }
    if (!kind || !primitiveRef) continue;
    const primitiveKind = primitiveKinds.get(primitiveRef);
    if (!primitiveKind) {
      errors.push(
        `${path}.primitiveRef references missing primitive ${primitiveRef}`,
      );
      continue;
    }
    const compilerContract =
      ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS[primitiveKind];
    if (
      !(compilerContract.stateRelationKinds as readonly string[]).includes(kind)
    ) {
      errors.push(
        `${path}.kind ${kind} is not allowed for primitive ${primitiveKind}`,
      );
    }
    if (effectBoundaryRef) {
      const boundary = effectBoundaries.get(effectBoundaryRef);
      const boundaryKind = boundary ? String(boundary.kind) : null;
      if (
        boundaryKind &&
        !(compilerContract.effectBoundaryKinds as readonly string[]).includes(
          boundaryKind,
        )
      ) {
        errors.push(
          `${path}.effectBoundaryRef kind ${boundaryKind} is not allowed for primitive ${primitiveKind}`,
        );
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

export function validateActionFlowApplicationArchetypeCorpus(
  value: unknown = ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS,
): ContractValidationResult {
  const errors: string[] = [];
  const corpus = Array.isArray(value) ? value : null;
  if (!corpus) return fail("actionflow archetype corpus must be an array");

  const seenScenarioIds = new Set<string>();
  const seenArchetypes = new Set<string>();
  for (const [index, rawScenario] of corpus.entries()) {
    const path = `actionflowArchetypeCorpus[${index}]`;
    const scenario = asRecord(rawScenario);
    if (!scenario) {
      errors.push(`${path} must be an object`);
      continue;
    }
    const scenarioId = expectStringValue(
      scenario,
      "scenarioId",
      errors,
      `${path}.scenarioId`,
    );
    const archetype = expectStringValue(
      scenario,
      "archetype",
      errors,
      `${path}.archetype`,
    );
    expectStringValue(scenario, "userGoal", errors, `${path}.userGoal`);
    expectStringValue(scenario, "userValue", errors, `${path}.userValue`);
    if ("persona" in scenario) {
      expectOptionalString(scenario, "persona", errors, `${path}.persona`);
    }
    if (scenarioId) {
      if (seenScenarioIds.has(scenarioId)) {
        errors.push(`${path}.scenarioId duplicates ${scenarioId}`);
      }
      seenScenarioIds.add(scenarioId);
    }
    if (archetype) {
      seenArchetypes.add(archetype);
      expectKnown(
        archetype,
        ACTIONFLOW_ONTOLOGY_V1_CONTRACT.applicationArchetypes,
        `${path}.archetype`,
        errors,
      );
      if (scenarioId && scenarioId !== archetype) {
        errors.push(`${path}.scenarioId must equal archetype ${archetype}`);
      }
    }
    validateClosedStringArray(
      scenario,
      "requiredPrimitiveKinds",
      ACTIONFLOW_PRIMITIVE_TAXONOMY.primitiveKinds,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "requiredStateScopes",
      ACTIONFLOW_TOPOLOGY_STATE_SCOPES,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "requiredEffectBoundaryKinds",
      ACTIONFLOW_PRIMITIVE_TAXONOMY.effectBoundaryKinds,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "requiredStateRelationKinds",
      ACTIONFLOW_PRIMITIVE_TAXONOMY.stateRelationKinds,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "requiredParamRoles",
      ACTIONFLOW_PRIMITIVE_TAXONOMY.paramRoles,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "requiredOutcomeCriterionKinds",
      ACTIONFLOW_ONTOLOGY_V1_CONTRACT.outcomeCriterionKinds,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "requiredReadinessStages",
      ACTIONFLOW_ONTOLOGY_V1_CONTRACT.readinessStages,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "failureModes",
      ACTIONFLOW_ONTOLOGY_V1_CONTRACT.failureModes,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "recoveryStrategies",
      ACTIONFLOW_ONTOLOGY_V1_CONTRACT.recoveryStrategies,
      errors,
      path,
    );
    validateClosedStringArray(
      scenario,
      "guardPolicies",
      ACTIONFLOW_ONTOLOGY_V1_CONTRACT.guardPolicies,
      errors,
      path,
    );
    for (const stage of ACTIONFLOW_ONTOLOGY_V1_CONTRACT.readinessStages) {
      const stages = Array.isArray(scenario.requiredReadinessStages)
        ? scenario.requiredReadinessStages
        : [];
      if (!stages.includes(stage)) {
        errors.push(`${path}.requiredReadinessStages must include ${stage}`);
      }
    }
    validateCorpusTopologyFit(scenario, errors, path);
  }

  for (const archetype of ACTIONFLOW_ONTOLOGY_V1_CONTRACT.applicationArchetypes) {
    if (!seenArchetypes.has(archetype)) {
      errors.push(`actionflow archetype corpus must include ${archetype}`);
    }
  }
  return { ok: errors.length === 0, errors };
}

function validateCorpusTopologyFit(
  scenario: Record<string, unknown>,
  errors: string[],
  path: string,
): void {
  const scopes = new Set(expectStringArray(scenario, "requiredStateScopes", errors, `${path}.requiredStateScopes`));
  const primitiveKinds = expectStringArray(
    scenario,
    "requiredPrimitiveKinds",
    errors,
    `${path}.requiredPrimitiveKinds`,
  );
  const requiredEffectBoundaryKinds = expectStringArray(
    scenario,
    "requiredEffectBoundaryKinds",
    errors,
    `${path}.requiredEffectBoundaryKinds`,
  );
  const requiredStateRelationKinds = expectStringArray(
    scenario,
    "requiredStateRelationKinds",
    errors,
    `${path}.requiredStateRelationKinds`,
  );
  const failureModes = expectStringArray(
    scenario,
    "failureModes",
    errors,
    `${path}.failureModes`,
  );
  const supportedEffectBoundaryKinds = new Set<string>();
  const supportedStateRelationKinds = new Set<string>();
  for (const primitiveKind of primitiveKinds) {
    const compilerContract =
      ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS[
        primitiveKind as keyof typeof ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS
      ];
    if (!compilerContract) continue;
    compilerContract.effectBoundaryKinds.forEach((kind) =>
      supportedEffectBoundaryKinds.add(kind),
    );
    compilerContract.stateRelationKinds.forEach((kind) =>
      supportedStateRelationKinds.add(kind),
    );
    if (
      primitiveKind.includes("datasource") &&
      !scopes.has("datasource")
    ) {
      errors.push(
        `${path}.requiredStateScopes must include datasource for primitive ${primitiveKind}`,
      );
    }
    if (primitiveKind.includes("local") && !scopes.has("local")) {
      errors.push(
        `${path}.requiredStateScopes must include local for primitive ${primitiveKind}`,
      );
    }
    for (const role of compilerContract.paramRoles) {
      const roles = Array.isArray(scenario.requiredParamRoles)
        ? scenario.requiredParamRoles
        : [];
      if (!roles.includes(role)) {
        errors.push(
          `${path}.requiredParamRoles must include ${role} for primitive ${primitiveKind}`,
        );
      }
    }
  }
  const hasDeclaredCapabilityGap =
    failureModes.includes("unsupported-primitive") ||
    failureModes.includes("retrieval-gap");
  for (const kind of requiredEffectBoundaryKinds) {
    if (!supportedEffectBoundaryKinds.has(kind) && !hasDeclaredCapabilityGap) {
      errors.push(
        `${path}.failureModes must include unsupported-primitive or retrieval-gap for unsupported effect boundary ${kind}`,
      );
    }
  }
  for (const kind of requiredStateRelationKinds) {
    if (!supportedStateRelationKinds.has(kind) && !hasDeclaredCapabilityGap) {
      errors.push(
        `${path}.failureModes must include unsupported-primitive or retrieval-gap for unsupported state relation ${kind}`,
      );
    }
  }
  for (const scope of scopes) {
    if (!ACTIONFLOW_TOPOLOGY_STATE_SCOPE_SET.has(scope)) {
      errors.push(`${path}.requiredStateScopes contains unknown scope ${scope}`);
    }
  }
}

function validateClosedStringArray(
  scenario: Record<string, unknown>,
  key: string,
  allowedValues: readonly string[],
  errors: string[],
  path: string,
): void {
  const values = expectStringArray(scenario, key, errors, `${path}.${key}`);
  if (values.length === 0) {
    errors.push(`${path}.${key} must include at least one value`);
  }
  for (const [index, value] of values.entries()) {
    expectKnown(value, allowedValues, `${path}.${key}[${index}]`, errors);
  }
}

function validateCompilerParamBindings(
  rawBindings: unknown[],
  allowedRoles: readonly string[],
  nodes: ReadonlyMap<string, Record<string, unknown>>,
  errors: string[],
  path: string,
): void {
  const seenRoles = new Set<string>();
  for (const [index, rawBinding] of rawBindings.entries()) {
    const bindingPath = `${path}[${index}]`;
    const binding = asRecord(rawBinding);
    if (!binding) {
      errors.push(`${bindingPath} must be an object`);
      continue;
    }
    const role = expectStringValue(
      binding,
      "role",
      errors,
      `${bindingPath}.role`,
    );
    expectStringValue(binding, "param", errors, `${bindingPath}.param`);
    const evidenceRefs = expectStringArray(
      binding,
      "evidenceRefs",
      errors,
      `${bindingPath}.evidenceRefs`,
    );
    const sourceNodeId =
      "sourceNodeId" in binding
        ? expectOptionalString(
            binding,
            "sourceNodeId",
            errors,
            `${bindingPath}.sourceNodeId`,
          )
        : null;
    const targetNodeId =
      "targetNodeId" in binding
        ? expectOptionalString(
            binding,
            "targetNodeId",
            errors,
            `${bindingPath}.targetNodeId`,
          )
        : null;
    const expression =
      "expression" in binding
        ? expectOptionalString(
            binding,
            "expression",
            errors,
            `${bindingPath}.expression`,
          )
        : null;
    if (expression) {
      validateExpressionOntology(expression, nodes, errors, `${bindingPath}.expression`);
    }
    if (typeof binding.value === "string" && binding.value.includes("#{")) {
      validateExpressionOntology(
        binding.value,
        nodes,
        errors,
        `${bindingPath}.value`,
      );
    }
    if ("resultKey" in binding) {
      expectOptionalString(
        binding,
        "resultKey",
        errors,
        `${bindingPath}.resultKey`,
      );
    }
    if ("value" in binding && binding.value === undefined) {
      errors.push(`${bindingPath}.value must not be undefined when present`);
    }
    if (evidenceRefs.length === 0) {
      errors.push(
        `${bindingPath}.evidenceRefs must include at least one evidence reference`,
      );
    }
    if (sourceNodeId && !nodes.has(sourceNodeId)) {
      errors.push(
        `${bindingPath}.sourceNodeId references missing node ${sourceNodeId}`,
      );
    }
    if (targetNodeId && !nodes.has(targetNodeId)) {
      errors.push(
        `${bindingPath}.targetNodeId references missing node ${targetNodeId}`,
      );
    }
    if (!role) continue;
    expectKnown(
      role,
      ACTIONFLOW_PRIMITIVE_TAXONOMY.paramRoles,
      `${bindingPath}.role`,
      errors,
    );
    if (!(allowedRoles as readonly string[]).includes(role)) {
      errors.push(
        `${bindingPath}.role ${role} is not allowed for this primitive`,
      );
    }
    seenRoles.add(role);
  }
  for (const role of allowedRoles) {
    if (!seenRoles.has(role)) {
      errors.push(`${path} must include role ${role}`);
    }
  }
}

function validateExpressionOntology(
  value: string,
  nodes: ReadonlyMap<string, Record<string, unknown>>,
  errors: string[],
  path: string,
): void {
  const expressions = [...value.matchAll(/#\{([^}]*)\}/g)];
  if (expressions.length === 0) {
    errors.push(`${path} must contain a #{...} expression`);
    return;
  }
  const nodePaths = [...nodes.values()]
    .map((node) => (typeof node.path === "string" ? node.path : null))
    .filter((nodePath): nodePath is string => Boolean(nodePath));
  for (const [expressionIndex, match] of expressions.entries()) {
    const expression = match[1]?.trim() ?? "";
    const expressionPath = `${path}#${expressionIndex}`;
    if (expression.length === 0) {
      errors.push(`${expressionPath} must not be empty`);
      continue;
    }
    validateExpressionPrefixes(expression, nodePaths, errors, expressionPath);
    validateExpressionAccessors(expression, errors, expressionPath);
  }
}

function topologyNodes(value: unknown): Map<string, Record<string, unknown>> {
  const topology = asRecord(value);
  if (!topology) return new Map();
  const nodes = expectArray(topology, "nodes", []);
  const result = new Map<string, Record<string, unknown>>();
  for (const [index, rawNode] of nodes.entries()) {
    const node = asRecord(rawNode);
    if (!node || typeof node.path !== "string") continue;
    const id =
      typeof node.id === "string" && node.id.trim()
        ? node.id
        : `node:${index}`;
    result.set(id, node);
  }
  return result;
}

function nodesFromPaths(
  nodePaths: readonly string[],
): Map<string, Record<string, unknown>> {
  return new Map(
    nodePaths.map((nodePath, index) => [
      `node:${index}`,
      { id: `node:${index}`, path: nodePath },
    ]),
  );
}

function validateExpressionPrefixes(
  expression: string,
  nodePaths: readonly string[],
  errors: string[],
  path: string,
): void {
  const prefixPattern = /\b([A-Za-z_][A-Za-z0-9_]*)\.([A-Za-z0-9_[\].-]+)/g;
  for (const match of expression.matchAll(prefixPattern)) {
    const prefix = match[1] ?? "";
    const suffix = match[2] ?? "";
    if (prefix === "Math") continue;
    if (EXPRESSION_LEGACY_PREFIXES.has(prefix)) {
      errors.push(`${path} prefix ${prefix} is legacy; use canonical expression ontology prefixes`);
      continue;
    }
    if (!EXPRESSION_ALLOWED_PREFIXES.has(prefix)) {
      errors.push(`${path} prefix ${prefix} is not registered in expression ontology`);
      continue;
    }
    if (EXPRESSION_NODE_BACKED_PREFIXES.has(prefix)) {
      const referencedPath = `${prefix}.${suffix}`.replace(/\[(.*?)\]/g, "");
      const hasNode = nodePaths.some(
        (nodePath) => referencedPath === nodePath || referencedPath.startsWith(`${nodePath}.`),
      );
      if (!hasNode) {
        errors.push(`${path} reference ${referencedPath} does not match a topology state node`);
      }
    }
  }
}

function validateExpressionAccessors(
  expression: string,
  errors: string[],
  path: string,
): void {
  const seen = new Set<string>();
  for (const match of expression.matchAll(/\.([A-Za-z_$][A-Za-z0-9_$]*)\s*\(/g)) {
    const accessor = match[1];
    if (accessor) seen.add(accessor);
  }
  for (const match of expression.matchAll(/(^|[^.\w$])([A-Za-z_$][A-Za-z0-9_$]*)\s*\(/g)) {
    const accessor = match[2];
    if (accessor) seen.add(accessor);
  }
  for (const accessor of seen) {
    if (!EXPRESSION_ALLOWED_ACCESSORS.has(accessor)) {
      errors.push(`${path} accessor ${accessor} is not registered in expression ontology`);
    }
  }
}

export function validateKgObservationEvidence(
  value: unknown,
): ContractValidationResult {
  const errors: string[] = [];
  const observation = asRecord(value);
  if (!observation) return fail("kg observation must be an object");
  expectString(observation, "id", errors, /^kgobs:/);
  expectOneOf(
    observation,
    "evidenceType",
    KG_OBSERVATION_CONTRACT.evidenceTypes,
    errors,
  );
  expectNumber(observation, "confidence", errors);
  expectOneOf(
    observation,
    "validationResult",
    KG_OBSERVATION_CONTRACT.validationResults,
    errors,
  );
  expectNumber(observation, "repeatedEvidenceCount", errors);
  expectString(observation, "summary", errors);
  expectRecord(observation, "facts", errors);
  if (
    typeof observation.confidence === "number" &&
    (observation.confidence < 0 || observation.confidence > 1)
  ) {
    errors.push("confidence must be between 0 and 1");
  }
  if (
    typeof observation.repeatedEvidenceCount === "number" &&
    observation.repeatedEvidenceCount < 1
  ) {
    errors.push("repeatedEvidenceCount must be >= 1");
  }
  if (
    observation.evidenceType === "fallback-promotion-readiness" &&
    typeof observation.normalizedTarget !== "string"
  ) {
    errors.push("fallback-promotion-readiness requires normalizedTarget");
  }
  if (
    (observation.evidenceType === "actionflow-runtime-replay" ||
      observation.evidenceType === "actionflow-structural-trace") &&
    typeof observation.traceId !== "string"
  ) {
    errors.push(`${String(observation.evidenceType)} requires traceId`);
  }
  return { ok: errors.length === 0, errors };
}

export function validateAgenticConformanceReleaseReport(
  value: unknown,
): ContractValidationResult {
  const errors: string[] = [];
  const report = asRecord(value);
  if (!report) return fail("release report must be an object");
  expectLiteral(
    report,
    "contractVersion",
    AGENTIC_CONFORMANCE_RELEASE_CONTRACT.version,
    errors,
  );
  if (typeof report.ok !== "boolean") errors.push("ok must be a boolean");
  expectArray(report, "failures", errors);
  expectRecord(report, "summary", errors);
  const requiredRows = expectRecord(report, "requiredRows", errors);
  if (requiredRows) {
    validateDatasourceStateScopeRow(requiredRows.datasourceStateScope, errors);
    validateDatasourceActionFlowScenarioRow(
      requiredRows.datasourceActionFlowScenarios,
      errors,
    );
    validateRendererFallbackRow(requiredRows.rendererFallback, errors);
    validateRendererReflectionRow(requiredRows.rendererReflection, errors);
    validateActionRegistryCoverageRow(
      requiredRows.actionRegistryCoverage,
      errors,
    );
    validatePromotionLifecycleRow(requiredRows.promotionLifecycle, errors);
    for (const key of [
      "runtimeReplay",
      "structuralTrace",
      "promotionReadiness",
    ]) {
      const row = asRecord(requiredRows[key]);
      if (!row) {
        errors.push(`requiredRows.${key} must be an object`);
        continue;
      }
      if (key !== "promotionReadiness") {
        const traceShape = {
          contractVersion: row.contractVersion,
          traceId: row.traceId,
          flowName: row.flowName,
          trigger: key === "runtimeReplay" ? "event" : "event",
          startState: {},
          finalState: {},
          status: row.status,
          steps: [],
        };
        errors.push(
          ...validateActionFlowTraceContract(traceShape).errors.filter(
            (error) => !error.includes("steps"),
          ),
        );
      }
      if (!("kgObservation" in row)) {
        errors.push(`requiredRows.${key}.kgObservation is required`);
      } else {
        errors.push(
          ...validateKgObservationEvidence(row.kgObservation).errors.map(
            (error) => `requiredRows.${key}.kgObservation.${error}`,
          ),
        );
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

function validateDatasourceActionFlowScenarioRow(
  value: unknown,
  errors: string[],
): void {
  const row = asRecord(value);
  if (!row) {
    errors.push("requiredRows.datasourceActionFlowScenarios must be an object");
    return;
  }
  const scenarios = expectArray(row, "scenarios", errors);
  const seen = new Set<string>();
  for (const [index, rawScenario] of scenarios.entries()) {
    const scenario = asRecord(rawScenario);
    if (!scenario) {
      errors.push(
        `requiredRows.datasourceActionFlowScenarios.scenarios[${index}] must be an object`,
      );
      continue;
    }
    expectString(scenario, "id", errors);
    expectOneOf(
      scenario,
      "dataSource",
      ACTIONFLOW_DATASOURCE_SCENARIOS,
      errors,
    );
    expectNumber(scenario, "eventBindingCount", errors);
    expectNumber(scenario, "flowCount", errors);
    expectNumber(scenario, "actionStepCount", errors);
    if (scenario.genericActionFlow !== true) {
      errors.push(
        `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].genericActionFlow must be true`,
      );
    }
    if (typeof scenario.dataSource === "string") {
      seen.add(scenario.dataSource);
    }
    const stateScopes = expectRecord(scenario, "stateScopes", errors);
    if (stateScopes?.unclassified !== 0) {
      errors.push(
        `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].stateScopes.unclassified must be 0`,
      );
    }
    const dataSources = expectRecord(scenario, "dataSources", errors);
    if (dataSources) {
      if (dataSources.unknown !== 0) {
        errors.push(
          `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].dataSources.unknown must be 0`,
        );
      }
      const dataSource =
        typeof scenario.dataSource === "string" ? scenario.dataSource : null;
      const dataSourceCount = dataSource ? dataSources[dataSource] : undefined;
      if (dataSource && typeof dataSourceCount !== "number") {
        errors.push(
          `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].dataSources.${dataSource} must be a number`,
        );
      } else if (typeof dataSourceCount === "number" && dataSourceCount < 1) {
        errors.push(
          `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].dataSources.${dataSource} must be >= 1`,
        );
      }
    }
    validateDatasourceScenarioExecutionRow(scenario.execution, index, errors);
  }
  for (const scenario of ACTIONFLOW_DATASOURCE_SCENARIOS) {
    if (!seen.has(scenario)) {
      errors.push(
        `requiredRows.datasourceActionFlowScenarios.scenarios must include ${scenario}`,
      );
    }
  }
}

function validateDatasourceScenarioExecutionRow(
  value: unknown,
  index: number,
  errors: string[],
): void {
  const execution = asRecord(value);
  if (!execution) {
    errors.push(
      `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].execution must be an object`,
    );
    return;
  }
  expectOneOf(execution, "dataSource", ACTIONFLOW_DATASOURCE_SCENARIOS, errors);
  expectOneOf(execution, "trigger", ACTIONFLOW_TRACE_CONTRACT.triggers, errors);
  expectString(
    execution,
    "traceId",
    errors,
    ACTIONFLOW_TRACE_CONTRACT.traceIdPattern,
  );
  expectLiteral(execution, "status", "ok", errors);
  expectString(execution, "boundElementId", errors);
  expectString(execution, "beforeText", errors);
  expectString(execution, "afterText", errors);
  const changedPaths = expectArray(execution, "changedPaths", errors);
  if (!changedPaths.includes("summary")) {
    errors.push(
      `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].execution.changedPaths must include summary`,
    );
  }
  if (execution.beforeText === execution.afterText) {
    errors.push(
      `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].execution.afterText must differ from beforeText`,
    );
  }
  if (execution.rendererReflected !== true) {
    errors.push(
      `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].execution.rendererReflected must be true`,
    );
  }
  if (!("kgObservation" in execution)) {
    errors.push(
      `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].execution.kgObservation is required`,
    );
  } else {
    errors.push(
      ...validateKgObservationEvidence(execution.kgObservation).errors.map(
        (error) =>
          `requiredRows.datasourceActionFlowScenarios.scenarios[${index}].execution.kgObservation.${error}`,
      ),
    );
  }
}

function validateActionRegistryCoverageRow(
  value: unknown,
  errors: string[],
): void {
  const row = asRecord(value);
  if (!row) {
    errors.push("requiredRows.actionRegistryCoverage must be an object");
    return;
  }
  const coveredActions = expectArray(row, "coveredActions", errors);
  const traceIds = expectArray(row, "traceIds", errors);
  for (const action of ACTIONFLOW_RELEASE_ACTION_COVERAGE) {
    if (!coveredActions.includes(action)) {
      errors.push(
        `requiredRows.actionRegistryCoverage.coveredActions must include ${action}`,
      );
    }
  }
  if (traceIds.length < 2) {
    errors.push(
      "requiredRows.actionRegistryCoverage.traceIds must include at least 2 traces",
    );
  }
  if (row.toastObserved !== true)
    errors.push(
      "requiredRows.actionRegistryCoverage.toastObserved must be true",
    );
  if (row.navigationObserved !== true)
    errors.push(
      "requiredRows.actionRegistryCoverage.navigationObserved must be true",
    );
}

function validatePromotionLifecycleRow(value: unknown, errors: string[]): void {
  const row = asRecord(value);
  if (!row) {
    errors.push("requiredRows.promotionLifecycle must be an object");
    return;
  }
  const statuses = expectArray(row, "statuses", errors);
  for (const status of FALLBACK_PROMOTION_LIFECYCLE_STATUSES) {
    if (!statuses.includes(status)) {
      errors.push(
        `requiredRows.promotionLifecycle.statuses must include ${status}`,
      );
    }
  }
  if (row.autoPromotionBlocked !== true) {
    errors.push(
      "requiredRows.promotionLifecycle.autoPromotionBlocked must be true",
    );
  }
}

function validateDatasourceStateScopeRow(
  value: unknown,
  errors: string[],
): void {
  const row = asRecord(value);
  if (!row) {
    errors.push("requiredRows.datasourceStateScope must be an object");
    return;
  }
  expectNumber(row, "eventBindingCount", errors);
  expectNumber(row, "flowCount", errors);
  expectNumber(row, "actionStepCount", errors);
  if (row.genericActionFlow !== true) {
    errors.push(
      "requiredRows.datasourceStateScope.genericActionFlow must be true",
    );
  }
  const stateScopes = expectRecord(row, "stateScopes", errors);
  if (stateScopes) {
    for (const key of ACTIONFLOW_STATE_SCOPE_TAXONOMY.stateScopes) {
      expectNumber(stateScopes, key, errors);
    }
    if (stateScopes.unclassified !== 0) {
      errors.push(
        "requiredRows.datasourceStateScope.stateScopes.unclassified must be 0",
      );
    }
  }
  const dataSources = expectRecord(row, "dataSources", errors);
  if (dataSources) {
    for (const key of ACTIONFLOW_STATE_SCOPE_TAXONOMY.dataSources) {
      expectNumber(dataSources, key, errors);
    }
    if (dataSources.unknown !== 0) {
      errors.push(
        "requiredRows.datasourceStateScope.dataSources.unknown must be 0",
      );
    }
  }
}

function validateRendererReflectionRow(value: unknown, errors: string[]): void {
  const row = asRecord(value);
  if (!row) {
    errors.push("requiredRows.rendererReflection must be an object");
    return;
  }
  expectString(row, "boundElementId", errors);
  expectString(row, "beforeText", errors);
  expectString(row, "afterText", errors);
  expectString(row, "changedPath", errors);
  if (row.beforeText === row.afterText) {
    errors.push(
      "requiredRows.rendererReflection.afterText must differ from beforeText",
    );
  }
  if (typeof row.afterText === "string" && !row.afterText.includes("Ada")) {
    errors.push(
      "requiredRows.rendererReflection.afterText must include reflected state",
    );
  }
}

function validateRendererFallbackRow(value: unknown, errors: string[]): void {
  const row = asRecord(value);
  if (!row) {
    errors.push("requiredRows.rendererFallback must be an object");
    return;
  }
  expectNumber(row, "fallbackCandidateCount", errors);
  expectNumber(row, "negativeConstraintCount", errors);
  expectNumber(row, "promotionPatchOperationCount", errors);
  const statusCounts = expectRecord(
    row,
    "promotionCandidateStatusCounts",
    errors,
  );
  if (
    typeof row.fallbackCandidateCount === "number" &&
    row.fallbackCandidateCount < 1
  ) {
    errors.push(
      "requiredRows.rendererFallback.fallbackCandidateCount must be >= 1",
    );
  }
  if (
    typeof row.negativeConstraintCount === "number" &&
    row.negativeConstraintCount < 1
  ) {
    errors.push(
      "requiredRows.rendererFallback.negativeConstraintCount must be >= 1",
    );
  }
  if (row.promotionPatchOperationCount !== 0) {
    errors.push(
      "requiredRows.rendererFallback.promotionPatchOperationCount must be 0",
    );
  }
  if (statusCounts && typeof statusCounts.quarantined !== "number") {
    errors.push(
      "requiredRows.rendererFallback.promotionCandidateStatusCounts.quarantined must be a number",
    );
  }
}

function fail(error: string): ContractValidationResult {
  return { ok: false, errors: [error] };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function expectLiteral(
  record: Record<string, unknown>,
  key: string,
  expected: string,
  errors: string[],
): void {
  if (record[key] !== expected) errors.push(`${key} must be ${expected}`);
}

function expectString(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
  pattern?: RegExp,
): void {
  const value = record[key];
  if (typeof value !== "string" || value.length === 0) {
    errors.push(`${key} must be a non-empty string`);
    return;
  }
  if (pattern && !pattern.test(value)) errors.push(`${key} has invalid format`);
}

function expectNumber(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
): void {
  if (typeof record[key] !== "number" || !Number.isFinite(record[key])) {
    errors.push(`${key} must be a finite number`);
  }
}

function expectOneOf(
  record: Record<string, unknown>,
  key: string,
  values: readonly string[],
  errors: string[],
): void {
  if (!values.includes(String(record[key]))) {
    errors.push(`${key} must be one of ${values.join(", ")}`);
  }
}

function expectKnown(
  value: string,
  values: readonly string[],
  path: string,
  errors: string[],
): boolean {
  if (!values.includes(value)) {
    errors.push(`${path} must be one of ${values.join(", ")}`);
    return false;
  }
  return true;
}

function expectStringValue(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
  path = key,
  pattern?: RegExp,
): string | null {
  const value = record[key];
  if (typeof value !== "string" || value.length === 0) {
    errors.push(`${path} must be a non-empty string`);
    return null;
  }
  if (pattern && !pattern.test(value))
    errors.push(`${path} has invalid format`);
  return value;
}

function expectOptionalString(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
  path = key,
): string | null {
  if (!(key in record) || record[key] === undefined) return null;
  return expectStringValue(record, key, errors, path);
}

function expectBoolean(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
  path = key,
): boolean | null {
  const value = record[key];
  if (typeof value !== "boolean") {
    errors.push(`${path} must be a boolean`);
    return null;
  }
  return value;
}

function expectStringArray(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
  path = key,
): string[] {
  const value = record[key];
  if (!Array.isArray(value)) {
    errors.push(`${path} must be an array`);
    return [];
  }
  const strings: string[] = [];
  for (const [index, item] of value.entries()) {
    if (typeof item !== "string" || item.length === 0) {
      errors.push(`${path}[${index}] must be a non-empty string`);
      continue;
    }
    strings.push(item);
  }
  return strings;
}

function expectRecord(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
): Record<string, unknown> | null {
  const value = asRecord(record[key]);
  if (!value) errors.push(`${key} must be an object`);
  return value;
}

function expectArray(
  record: Record<string, unknown>,
  key: string,
  errors: string[],
): unknown[] {
  const value = record[key];
  if (!Array.isArray(value)) {
    errors.push(`${key} must be an array`);
    return [];
  }
  return value;
}

function readPath(record: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((current, part) => {
    if (current === null || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[part];
  }, record);
}

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
